"use client";

import React from "react";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const formatDate = (dateString?: string) => {
  if (!dateString) return {
    day: "05",
    month: "Jun"
  };
  try {
    const date = new Date(dateString);
    return {
      day: date.toLocaleDateString("en-US", {
        day: "2-digit"
      }),
      month: date.toLocaleDateString("en-US", {
        month: "short"
      })
    };
  } catch (e) {
    return {
      day: "05",
      month: "Jun"
    };
  }
};
function BlogGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
        {[...Array(2)].map((_, i) => <Card key={i} data-index={i} className="flex flex-col sm:flex-row overflow-hidden border-border/20 shadow-md p-0 gap-0 animate-pulse bg-secondary">
            <div className="w-full sm:w-[45%] min-h-[250px] sm:min-h-[320px] bg-muted/20 shrink-0" />
            <CardContent className="w-full sm:w-[55%] p-6 sm:p-8 flex flex-col justify-center gap-4">
              <div className="h-4 w-1/3 bg-muted/20 rounded" />
              <div className="h-8 w-3/4 bg-muted/20 rounded" />
              <div className="h-16 w-full bg-muted/20 rounded" />
              <div className="h-10 w-32 bg-muted/20 rounded-md mt-4" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>Failed to load alumni memories.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>No alumni profiles found.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
      {posts.map((post, i) => {
      const {
        day,
        month
      } = formatDate(post.date);
      const category = post.categories?.[0]?.name || "Class of 2005";
      return <Card key={post.id} data-index={i} className="group flex flex-col sm:flex-row overflow-hidden border-accent/20 shadow-lg hover:shadow-xl transition-all duration-300 p-0 gap-0 bg-secondary backdrop-blur-md">
            {}
            <div className="relative w-full sm:w-[45%] min-h-[250px] sm:min-h-[320px] overflow-hidden shrink-0">
              <Link to={post.link || "#"} className="absolute inset-0 z-10">
                <span className="sr-only">View {post.title}</span>
              </Link>
              <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687108/img/senior_portrait_placeholder.png"} alt={post.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 grayscale hover:grayscale-0" />
              {}
              <div className="absolute top-4 left-4 z-20 bg-primary/90 backdrop-blur-sm text-primary-foreground px-4 py-2 rounded-md text-center shadow-md border border-accent/30">
                <div className="text-2xl font-bold font-default leading-none text-accent">{day}</div>
                <div className="text-xs uppercase tracking-widest mt-1 opacity-90 font-sans">{month}</div>
              </div>
            </div>

            {}
            <CardContent className="w-full sm:w-[55%] p-6 sm:p-8 flex flex-col justify-center">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <span className="text-accent font-medium font-sans tracking-wider uppercase">{category}</span>
                <span className="opacity-50">•</span>
                <span className="font-sans">Skyline High</span>
              </div>
              
              <h3 className="text-xl md:text-2xl font-bold font-default text-foreground mb-4 line-clamp-2 group-hover:text-accent transition-colors">
                <Link to={post.link || "#"}>{post.title}</Link>
              </h3>
              
              {post.excerpt && <div className="text-muted-foreground mb-6 line-clamp-3 text-sm md:text-base font-mono italic" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />}
              
              <div className="mt-auto">
                <Link to={post.link || "#"}>
                  <Button variant="default" className="rounded-md gap-2 font-semibold bg-gradient-to-r from-accent to-amber-600 hover:shadow-lg hover:shadow-accent/20 transition-all">
                    <Plus className="size-4" />
                    View Memory
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>;
    })}
    </div>;
}
export default function WhoSComing() {
  const wp_query_params = {
    post_type: "post",
    per_page: 2,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="254619" id="whos-coming" className="py-20 lg:py-32 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-accent font-semibold tracking-widest uppercase text-sm mb-4 block font-sans">
            Classmate Spotlight
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground">
            Who&apos;s Coming?
          </h2>
          <p className="mt-4 text-muted-foreground font-sans">
            Reconnect with old friends and browse through the memories shared by the Class of 2005.
          </p>
        </div>
        
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 2,
        order: "desc",
        orderby: "date"
      }}>
          <BlogGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}